import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  TextField as MuiTextField,
  FormControl as MuiFormControl,
  Autocomplete as MuiAutocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePtaContext } from "app/services/ptasContext";
import { getTaskByMatch, getPtaByMatch } from "app/services/ptasService";
import { ResetFilter } from "app/shared/ui/FilterPanel/ResetFilter";

export const PTAsFilter = () => {
  // For fetching up the multilingual text object using i18next.
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { ptaFilters, setPtaFilters, pagination, setPagination } =
    usePtaContext();

  // State variable and its function for Project Number
  const [project, setProject] = useState(ptaFilters.project);
  // State variable and its function Project Name
  const [projectName, setProjectName] = useState(ptaFilters.projectName);
  // State variable and its function Task Number
  const [task, setTask] = useState(ptaFilters.project ? ptaFilters.task : null);
  // State variable and its function Award Number
  const [award, setAward] = useState(ptaFilters.award);
  // State variable and its function Task Number
  const [taskOwner, setTaskOwner] = useState(ptaFilters.taskOwner);
  // State variable and its function Project Owner
  const [projectOwner, setProjectOwner] = useState(ptaFilters.projectOwner);
  /**
   * State variable and its function disabling/enabling
   * Task Number MuiAutocomplete box
   * */

  const [disableTaskNumber, setDisableTaskNumber] = useState(
    ptaFilters.task || ptaFilters.project ? false : true,
  );

  const [ptaList, setPtaList] = useState([]);

  const [taskList, setTaskList] = useState([]);

  // Function to reset the Filter value and re-load PTAs data accordingly
  const resetFn = () => {
    setProject("");
    setProjectName("");
    setTask("");
    setAward("");
    setTaskOwner("");
    setProjectOwner("");
    setDisableTaskNumber(true);
    setPtaList([]);
    setTaskList([]);
    setPtaFilters({
      project: "",
      task: "",
      award: "",
      projectName: "",
      projectOwner: "",
      taskOwner: "",
    });
  };

  const [ptaError, setPtaError] = useState({
    matchOn: null,
    exist: false,
    message: null,
  });

  const [taskError, setTaskError] = useState({
    exist: false,
    message: null,
  });

  // useEffect to act on the change of Filter Data variables.
  useEffect(() => {
    const params = {
      project,
      task,
      award,
      projectName,
      projectOwner,
      taskOwner,
    };
    const queryParams = Object.keys(params)
      .filter((key) => params[key])
      .map((key) => {
        return `${key}=${encodeURIComponent(params[key])}`;
      })
      .join("&");
    navigate(`${location.pathname}?${queryParams}`);
  }, [
    project,
    task,
    award,
    projectName,
    projectOwner,
    taskOwner,
    navigate,
    location.pathname,
  ]);

  return (
    <>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="projectFilters"
          options={ptaList || []}
          getOptionLabel={(option) => option}
          value={project}
          clearOnEscape
          loading={loading}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * 2. Loading the Task list of corresponding Project Number.
           * 3. Enabling the Task Number list after calling up the
           * function for loading up the data for Task list.
           * */
          onChange={(e, value) => {
            const project = value;
            setProject(project);
            setPtaFilters({
              ...ptaFilters,
              project,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
            setDisableTaskNumber(false);
          }}
          onOpen={(event) => {
            setPtaList([]);
          }}
          clearIcon={false}
          blurOnSelect
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("PTAs.filterPanelItems.options.project.label")}
              variant="outlined"
              onChange={(e, value) => {
                getPtaByMatch(
                  { matchOn: "projectNumber" },
                  e.target.value,
                  setPtaList,
                  setLoading,
                  setPtaError,
                );

                if (e.target.value === "" && task === null) {
                  setDisableTaskNumber(true);
                }
              }}
              error={ptaError.exist && ptaError.matchOn === "projectNumber"}
              helperText={
                ptaError.matchOn === "projectNumber" && ptaError.message
              }
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="taskFilters"
          options={taskList || []}
          getOptionLabel={(option) => option}
          value={task}
          clearOnEscape
          loading={loading}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * */
          onChange={(e, value) => {
            const task = value;
            setTask(task);
            setPtaFilters({
              ...ptaFilters,
              task,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onOpen={(event) => {
            setTaskList([]);
          }}
          clearIcon={false}
          blurOnSelect
          // Disabling/Enabling the Task Number MuiAutocomplete
          disabled={disableTaskNumber}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("PTAs.filterPanelItems.options.task.label")}
              variant="outlined"
              onChange={(e, value) => {
                getTaskByMatch(
                  e.target.value,
                  project,
                  setTaskList,
                  setLoading,
                  setTaskError,
                );

                if (e.target.value === "" && project === null)
                  setDisableTaskNumber(true);
              }}
              error={taskError.exist}
              helperText={taskError.message}
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="awardFilters"
          options={ptaList || []}
          getOptionLabel={(option) => option}
          value={award}
          clearOnEscape
          loading={loading}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * */
          onChange={(e, value) => {
            const award = value;
            setAward(award);
            setPtaFilters({
              ...ptaFilters,
              award,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onOpen={(event) => {
            setPtaList([]);
          }}
          clearIcon={false}
          blurOnSelect
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("PTAs.filterPanelItems.options.award.label")}
              variant="outlined"
              onChange={(e, value) => {
                getPtaByMatch(
                  { matchOn: "awardNumber" },
                  e.target.value,
                  setPtaList,
                  setLoading,
                  setPtaError,
                );
              }}
              error={ptaError.exist && ptaError.matchOn === "awardNumber"}
              helperText={
                ptaError.matchOn === "awardNumber" && ptaError.message
              }
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="projectNameFilters"
          options={ptaList || []}
          getOptionLabel={(option) => option}
          value={projectName}
          clearOnEscape
          loading={loading}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * */
          onChange={(e, value) => {
            const projectName = value;
            setProjectName(projectName);
            setPtaFilters({
              ...ptaFilters,
              projectName,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onOpen={(event) => {
            setPtaList([]);
          }}
          clearIcon={false}
          blurOnSelect
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("PTAs.filterPanelItems.options.projectName.label")}
              variant="outlined"
              onChange={(e, value) => {
                getPtaByMatch(
                  { matchOn: "projectName" },
                  e.target.value,
                  setPtaList,
                  setLoading,
                  setPtaError,
                );
              }}
              error={ptaError.exist && ptaError.matchOn === "projectName"}
              helperText={
                ptaError.matchOn === "projectName" && ptaError.message
              }
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="projectOwnerFilters"
          options={ptaList || []}
          getOptionLabel={(option) => option}
          value={projectOwner}
          clearOnEscape
          loading={loading}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * */
          onChange={(e, value) => {
            const projectOwner = value;
            setProjectOwner(projectOwner);
            setPtaFilters({
              ...ptaFilters,
              projectOwner,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onOpen={(event) => {
            setPtaList([]);
          }}
          clearIcon={false}
          blurOnSelect
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("PTAs.filterPanelItems.options.projectOwner.label")}
              variant="outlined"
              onChange={(e, value) => {
                getPtaByMatch(
                  { matchOn: "projectOwnerFullName" },
                  e.target.value,
                  setPtaList,
                  setLoading,
                  setPtaError,
                );
              }}
              error={
                ptaError.exist && ptaError.matchOn === "projectOwnerFullName"
              }
              helperText={
                ptaError.matchOn === "projectOwnerFullName" && ptaError.message
              }
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <MuiAutocomplete
          size="small"
          id="taskOwnerFilters"
          options={ptaList || []}
          getOptionLabel={(option) => option}
          value={taskOwner}
          clearOnEscape
          loading={loading}
          /**
           * On change event for MuiAutocomplete
           *
           * 1. Setting up the values for Filter via useState variable.
           * */
          onChange={(e, value) => {
            const taskOwner = value;
            setTaskOwner(taskOwner);
            setPtaFilters({
              ...ptaFilters,
              taskOwner,
            });
            setPagination({
              ...pagination,
              page: 0,
            });
          }}
          onOpen={(event) => {
            setPtaList([]);
          }}
          clearIcon={false}
          blurOnSelect
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={t("PTAs.filterPanelItems.options.taskOwner.label")}
              variant="outlined"
              onChange={(e, value) => {
                getPtaByMatch(
                  { matchOn: "taskOwnerFullName" },
                  e.target.value,
                  setPtaList,
                  setLoading,
                  setPtaError,
                );
              }}
              error={ptaError.exist && ptaError.matchOn === "taskOwnerFullName"}
              helperText={
                ptaError.matchOn === "taskOwnerFullName" && ptaError.message
              }
            />
          )}
        />
      </MuiFormControl>
      <ResetFilter resetFn={resetFn} />
    </>
  );
};
